export default function CircularProgress({ progress }: { progress: number }) {
  // Calculate the arc path for the progress
  const radius = 60;
  const centerX = 96;
  const centerY = 92;

  // For 100%, cap at 99.9% to avoid SVG arc issue (start and end point being the same)
  const cappedProgress = progress >= 100 ? 99.99 : progress;

  // Calculate end angle based on progress (0% = -90deg, 100% = 270deg)
  const progressAngle = (cappedProgress / 100) * 360 - 90;
  const progressRadians = (progressAngle * Math.PI) / 180;

  // Calculate end point of the arc
  const endX = centerX + radius * Math.cos(progressRadians);
  const endY = centerY + radius * Math.sin(progressRadians);

  // Determine if we need a large arc (more than 180 degrees)
  const largeArcFlag = cappedProgress > 50 ? 1 : 0;

  // Create the arc path (starting from top: 96, 32)
  const arcPath = `M ${centerX} ${centerY - radius} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${endX} ${endY}`;

  const isComplete = progress >= 100;

  return progress !== 0 ? (
    <svg
      width="184"
      height="184"
      viewBox="4 4 184 184"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Outer dark circle with glow */}
      <g filter="url(#filter0_d_31067_127889)">
        <circle
          cx="96"
          cy="92"
          r="60"
          stroke="var(--color-surface-3)"
          strokeWidth="8"
          fill="none"
        />
      </g>

      {/* Progress arc - green when 100%, amethyst gradient otherwise */}
      {progress > 0 && (
        <path
          d={arcPath}
          stroke={isComplete ? 'var(--Color-Extended-Green-Green-Shade)' : 'url(#paint0_linear_31067_127889)'}
          strokeWidth="8"
          fill="none"
          strokeLinecap="butt"
          style={{
            transition: 'stroke 0.3s ease, d 0.5s ease',
          }}
        />
      )}

      {/* Inner circles */}
      <circle cx="96" cy="92" r="45.5" fill="var(--color-surface-2)" />
      <circle cx="96" cy="92" r="45.5" fill="url(#paint1_linear_31067_127889)" fillOpacity="1" />
      <circle cx="96" cy="92" r="45.5" stroke="#000000" strokeOpacity="0.2" fill="none" />
      <circle
        cx="96"
        cy="92"
        r="45.5"
        stroke="url(#paint2_linear_31067_127889)"
        strokeOpacity="1"
        fill="none"
      />

      {/* Percentage text - green when 100%, amethyst otherwise */}
      <text
        x="96"
        y="94"
        fontFamily="Inter"
        fontWeight="600"
        fontSize="18"
        textAnchor="middle"
        fill={isComplete ? 'var(--Color-Extended-Green-Green-Shade)' : 'var(--Color-Extended-Amethyst-Amethyst-Light)'}
      >
        {progress}%
      </text>

      {/* Complete text */}
      <path
        d="M76.2104 101H75.1536C75.0911 100.696 74.9817 100.429 74.8255 100.199C74.6721 99.969 74.4846 99.776 74.263 99.619C74.0442 99.46 73.8013 99.341 73.5343 99.261C73.2672 99.182 72.9888 99.142 72.699 99.142C72.1706 99.142 71.6919 99.276 71.263 99.543C70.8368 99.81 70.4973 100.203 70.2445 100.723C69.9945 101.243 69.8695 101.881 69.8695 102.636C69.8695 103.392 69.9945 104.03 70.2445 104.55C70.4973 105.07 70.8368 105.463 71.263 105.73C71.6919 105.997 72.1706 106.131 72.699 106.131C72.9888 106.131 73.2672 106.091 73.5343 106.011C73.8013 105.932 74.0442 105.814 74.263 105.658C74.4846 105.499 74.6721 105.304 74.8255 105.074C74.9817 104.841 75.0911 104.574 75.1536 104.273H76.2104C76.1309 104.719 75.986 105.118 75.7757 105.47C75.5655 105.822 75.3042 106.122 74.9917 106.369C74.6792 106.614 74.3283 106.8 73.9391 106.928C73.5527 107.055 73.1394 107.119 72.699 107.119C71.9547 107.119 71.2928 106.938 70.7132 106.574C70.1337 106.21 69.6777 105.693 69.3453 105.023C69.013 104.352 68.8468 103.557 68.8468 102.636C68.8468 101.716 69.013 100.92 69.3453 100.25C69.6777 99.58 70.1337 99.062 70.7132 98.699C71.2928 98.335 71.9547 98.153 72.699 98.153C73.1394 98.153 73.5527 98.217 73.9391 98.345C74.3283 98.473 74.6792 98.661 74.9917 98.908C75.3042 99.152 75.5655 99.45 75.7757 99.803C75.986 100.152 76.1309 100.551 76.2104 101ZM80.4409 107.136C79.85 107.136 79.3315 106.996 78.8855 106.714C78.4423 106.433 78.0957 106.04 77.8457 105.534C77.5985 105.028 77.475 104.437 77.475 103.761C77.475 103.08 77.5985 102.484 77.8457 101.976C78.0957 101.467 78.4423 101.072 78.8855 100.791C79.3315 100.51 79.85 100.369 80.4409 100.369C81.0318 100.369 81.5488 100.51 81.992 100.791C82.438 101.072 82.7846 101.467 83.0318 101.976C83.2818 102.484 83.4068 103.08 83.4068 103.761C83.4068 104.437 83.2818 105.028 83.0318 105.534C82.7846 106.04 82.438 106.433 81.992 106.714C81.5488 106.996 81.0318 107.136 80.4409 107.136ZM80.4409 106.233C80.8897 106.233 81.2591 106.118 81.5488 105.888C81.8386 105.658 82.0531 105.355 82.1923 104.98C82.3315 104.605 82.4011 104.199 82.4011 103.761C82.4011 103.324 82.3315 102.916 82.1923 102.538C82.0531 102.161 81.8386 101.855 81.5488 101.622C81.2591 101.389 80.8897 101.273 80.4409 101.273C79.992 101.273 79.6227 101.389 79.3329 101.622C79.0431 101.855 78.8287 102.161 78.6895 102.538C78.5502 102.916 78.4806 103.324 78.4806 103.761C78.4806 104.199 78.5502 104.605 78.6895 104.98C78.8287 105.355 79.0431 105.658 79.3329 105.888C79.6227 106.118 79.992 106.233 80.4409 106.233ZM84.9419 107V100.455H85.9135V101.477H85.9988C86.1351 101.128 86.3553 100.857 86.6593 100.663C86.9632 100.467 87.3283 100.369 87.7544 100.369C88.1863 100.369 88.5456 100.467 88.8326 100.663C89.1223 100.857 89.3482 101.128 89.5101 101.477H89.5783C89.7459 101.139 89.9973 100.871 90.3326 100.672C90.6678 100.47 91.0698 100.369 91.5385 100.369C92.1238 100.369 92.6025 100.553 92.9746 100.919C93.3468 101.283 93.5328 101.849 93.5328 102.619V107H92.5272V102.619C92.5272 102.136 92.3951 101.791 92.1309 101.584C91.8667 101.376 91.5556 101.273 91.1976 101.273C90.7374 101.273 90.3809 101.412 90.128 101.69C89.8752 101.966 89.7488 102.315 89.7488 102.739V107H88.726V102.517C88.726 102.145 88.6053 101.845 88.3638 101.618C88.1223 101.388 87.8113 101.273 87.4306 101.273C87.1692 101.273 86.9249 101.342 86.6976 101.482C86.4732 101.621 86.2914 101.814 86.1522 102.061C86.0158 102.305 85.9476 102.588 85.9476 102.909V107H84.9419ZM95.3716 109.455V100.455H96.3432V101.494H96.4625C96.5364 101.381 96.6387 101.236 96.7694 101.06C96.9029 100.881 97.0932 100.722 97.3404 100.582C97.5904 100.44 97.9284 100.369 98.3546 100.369C98.9057 100.369 99.3915 100.507 99.812 100.783C100.232 101.058 100.561 101.449 100.796 101.955C101.032 102.46 101.15 103.057 101.15 103.744C101.15 104.438 101.032 105.038 100.796 105.547C100.561 106.053 100.234 106.445 99.8162 106.723C99.3986 106.999 98.9171 107.136 98.3716 107.136C97.9512 107.136 97.6145 107.067 97.3617 106.928C97.1088 106.786 96.9142 106.625 96.7779 106.446C96.6415 106.264 96.5364 106.114 96.4625 105.994H96.3773V109.455H95.3716ZM96.3603 103.727C96.3603 104.222 96.4327 104.658 96.5776 105.036C96.7225 105.411 96.9341 105.705 97.2125 105.918C97.4909 106.128 97.8319 106.233 98.2353 106.233C98.6557 106.233 99.0066 106.122 99.2878 105.901C99.5719 105.676 99.785 105.375 99.927 104.997C100.072 104.616 100.144 104.193 100.144 103.727C100.144 103.267 100.073 102.852 99.9313 102.483C99.7921 102.111 99.5804 101.817 99.2963 101.601C99.0151 101.382 98.6614 101.273 98.2353 101.273C97.8262 101.273 97.4824 101.376 97.204 101.584C96.9256 101.788 96.7154 102.075 96.5733 102.445C96.4313 102.811 96.3603 103.239 96.3603 103.727ZM103.69 98.273V107H102.684V98.273H103.69ZM108.276 107.136C107.645 107.136 107.101 106.997 106.644 106.719C106.189 106.438 105.839 106.045 105.591 105.543C105.347 105.037 105.225 104.449 105.225 103.778C105.225 103.108 105.347 102.517 105.591 102.006C105.839 101.491 106.182 101.091 106.623 100.804C107.066 100.514 107.583 100.369 108.174 100.369C108.515 100.369 108.851 100.426 109.184 100.54C109.516 100.653 109.819 100.838 110.091 101.094C110.364 101.347 110.581 101.682 110.743 102.099C110.905 102.517 110.986 103.031 110.986 103.642V104.068H105.941V103.199H109.964C109.964 102.83 109.89 102.5 109.742 102.21C109.597 101.92 109.39 101.692 109.12 101.524C108.853 101.357 108.537 101.273 108.174 101.273C107.773 101.273 107.427 101.372 107.134 101.571C106.844 101.767 106.621 102.023 106.465 102.338C106.309 102.653 106.231 102.991 106.231 103.352V103.932C106.231 104.426 106.316 104.845 106.486 105.189C106.66 105.53 106.9 105.79 107.206 105.969C107.513 106.145 107.87 106.233 108.276 106.233C108.54 106.233 108.779 106.196 108.992 106.122C109.208 106.045 109.394 105.932 109.55 105.781C109.706 105.628 109.827 105.437 109.912 105.21L110.884 105.483C110.782 105.812 110.61 106.102 110.368 106.352C110.127 106.599 109.829 106.793 109.474 106.932C109.118 107.068 108.719 107.136 108.276 107.136ZM115.363 100.455V101.307H111.971V100.455H115.363ZM112.959 98.886H113.965V105.125C113.965 105.409 114.006 105.622 114.089 105.764C114.174 105.903 114.282 105.997 114.412 106.045C114.546 106.091 114.687 106.114 114.834 106.114C114.945 106.114 115.036 106.108 115.107 106.097C115.178 106.082 115.235 106.071 115.278 106.062L115.482 106.966C115.414 106.991 115.319 107.017 115.197 107.043C115.074 107.071 114.92 107.085 114.732 107.085C114.448 107.085 114.17 107.024 113.897 106.902C113.627 106.78 113.403 106.594 113.224 106.344C113.047 106.094 112.959 105.778 112.959 105.398V98.886ZM119.55 107.136C118.919 107.136 118.375 106.997 117.917 106.719C117.463 106.438 117.112 106.045 116.865 105.543C116.621 105.037 116.498 104.449 116.498 103.778C116.498 103.108 116.621 102.517 116.865 102.006C117.112 101.491 117.456 101.091 117.896 100.804C118.339 100.514 118.856 100.369 119.447 100.369C119.788 100.369 120.125 100.426 120.457 100.54C120.79 100.653 121.092 100.838 121.365 101.094C121.638 101.347 121.855 101.682 122.017 102.099C122.179 102.517 122.26 103.031 122.26 103.642V104.068H117.214V103.199H121.237C121.237 102.83 121.163 102.5 121.015 102.21C120.871 101.92 120.663 101.692 120.393 101.524C120.126 101.357 119.811 101.273 119.447 101.273C119.047 101.273 118.7 101.372 118.407 101.571C118.118 101.767 117.895 102.023 117.738 102.338C117.582 102.653 117.504 102.991 117.504 103.352V103.932C117.504 104.426 117.589 104.845 117.76 105.189C117.933 105.53 118.173 105.79 118.48 105.969C118.787 106.145 119.143 106.233 119.55 106.233C119.814 106.233 120.052 106.196 120.265 106.122C120.481 106.045 120.667 105.932 120.824 105.781C120.98 105.628 121.101 105.437 121.186 105.21L122.157 105.483C122.055 105.812 121.883 106.102 121.642 106.352C121.4 106.599 121.102 106.793 120.747 106.932C120.392 107.068 119.993 107.136 119.55 107.136Z"
        fill="var(--color-text-default-light)"
      />
      <defs>
        <filter
          id="filter0_d_31067_127889"
          x="0"
          y="0"
          width="192"
          height="192"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dy="4" />
          <feGaussianBlur stdDeviation="16" />
          <feColorMatrix type="matrix" values="0 0 0 0 0.676 0 0 0 0 0.46 0 0 0 0 1 0 0 0 0.65 0" />
          <feBlend
            mode="normal"
            in2="BackgroundImageFix"
            result="effect1_dropShadow_31067_127889"
          />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="effect1_dropShadow_31067_127889"
            result="shape"
          />
        </filter>
        <linearGradient
          id="paint0_linear_31067_127889"
          x1="96"
          y1="28"
          x2="160"
          y2="156"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="var(--Color-Extended-Amethyst-Amethyst-Accent)" stopOpacity="0.95" />
          <stop
            offset="1"
            stopColor="var(--Color-Extended-Amethyst-Amethyst-Accent)"
            stopOpacity="0.15"
          />
        </linearGradient>
        <linearGradient
          id="paint1_linear_31067_127889"
          x1="96"
          y1="46"
          x2="96"
          y2="138"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="var(--Color-Extended-Amethyst-Amethyst-Accent)" stopOpacity="0.15" />
          <stop
            offset="1"
            stopColor="var(--Color-Extended-Amethyst-Amethyst-Accent)"
            stopOpacity="0"
          />
        </linearGradient>
        <linearGradient
          id="paint2_linear_31067_127889"
          x1="152.5"
          y1="184"
          x2="117"
          y2="105.5"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="var(--Color-Extended-Green-Green-Accent)" stopOpacity="1" />
          <stop offset="0.5" stopColor="var(--Color-Extended-Blue-Blue-Accent)" stopOpacity="0.6" />
          <stop
            offset="0.8"
            stopColor="var(--Color-Extended-Amethyst-Amethyst-Accent)"
            stopOpacity="0.3"
          />
          <stop
            offset="1"
            stopColor="var(--Color-Extended-Amethyst-Amethyst-Accent)"
            stopOpacity="0"
          />
        </linearGradient>
      </defs>
    </svg>
  ) : null;
}
